/** @jsxImportSource @emotion/react */
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect } from "react";
import { IAppState } from "../../core/redux/IAppState";
import { Loader } from "../../forms/elements/loader/Loader";
import styled from "@emotion/styled"
import { MediumText } from "../../styles/styles";
import { COOL_BLUE, COOLER_GREY, LIGHT_GREY, WHITE } from "../../styles/colors";
import { getResource } from "../../language/resources/getResource";
import { copyToClipboardAction } from "../../copyToClipboard/copyToClipboardAction";
import { GuestHydrationErrorReplayButton } from "./GuestHydrationErrorReplayButton";
import { getGuestHydrationErrorsAction } from "./getGuestHydrationErrorsAction";
import { getLoading } from "../../core/redux/api/module";
import { ACTION_TYPE } from "../../core/redux/actionType";
import TablePagination from "@mui/material/TablePagination";
import { GuestHydrationErrorReportData } from "../models";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from "./constants";
import { css } from "@emotion/react";
import { Dropdown } from "../../forms/elements/select/Dropdown";
import { BigNeutral } from "../../forms/elements/buttons";
import { IconButton, TextField } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import { formatDate } from "../../language/time";

function calculateFixedHeight(guestsPerPage: number): number {
  const rowHeight = 66;     // px per row
  const headerHeight = 50;  // px for table header
  const extraPadding = 30;  // px for spacing
  return headerHeight + (guestsPerPage * rowHeight) + extraPadding;
}

const modalContent = css`
  margin-top: 30px;

  /* min 960px, preferred 90vw, max 1600px */
  width: clamp(960px, 90vw, 1600px);

  /* center inside the dialog/modal */
  margin: 0 auto;

  /* breathing room inside */
  padding: 0 20px;

  /* prevent edge-to-edge on tiny viewports */
  max-width: 90vw;
`;

const tableWrapper = (heightPx: number) => css`
  height: ${heightPx}px;
  width: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges; /* prevent width jump when scrollbar appears */
`;

const loaderOverlay = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  pointer-events: none;
`;

const rowItemStyles = css`
  padding: 11px 10px;
  line-height: 22px;
  border: 1px solid transparent;
`;

const tableStyle = css`
  border-spacing: 0;
  width: 100%;
  min-width: 800px;
  table-layout: fixed;
  overflow-x: auto; /* allow horizontal scroll if needed */

  transition: all 0.5s ease;

  thead tr {
    ${MediumText};
    align-items: center;
    height: 50px;
  }

  tbody tr {
    ${MediumText};
    border-color: ${COOLER_GREY};
    background-color: ${WHITE};
    height: 65px;

    &:nth-of-type(odd) {
      background-color: ${LIGHT_GREY};
    }

    &:last-of-type {
      td {
        border-bottom: 1px solid ${COOLER_GREY};
      }
    }

    &:hover {
      background-color: ${COOL_BLUE};
    }
  }

  th {
    ${rowItemStyles};
    padding-top: 0;
    border-bottom: 0;
    text-align: left;
    overflow: hidden;         /* since table-layout: fixed */
    text-overflow: ellipsis;
    white-space: nowrap;
  }


  td {
    ${rowItemStyles};
    border-color: ${COOLER_GREY};
    border-right: 0;
    border-bottom: 0;
    height: 100%;
    overflow: hidden;         /* since table-layout: fixed */    
    text-overflow: ellipsis;
    white-space: nowrap;

    &:last-of-type {
      border-right: 1px solid ${COOLER_GREY};
    }
  }
`;

const overridemuifont = css`
  .MuiTablePagination-selectLabel {
    font-size: 1rem;
  }
  .MuiTablePagination-root {
    font-size: 1rem;
  }
  .MuiTablePagination-displayedRows {
    font-size: 1rem;
  }
  .MuiTablePagination-actions {
    font-size: 1rem;
  }
`;

const tablePagination = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 50px;
  ${overridemuifont}
`;

const MasterGuestId = styled("span")`
  min-width: 375px;
  cursor: pointer;
`;

const guestSearch = css`
  width: 300px;
  margin-right: 25px;
`

export const GuestHydrationErrorReport: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const resources = useSelector((state: IAppState) => state.locale);
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const isLoading = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_REQUEST,
        ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_REQUEST
      )
  );
  const reportData = useSelector(
    (state: IAppState) => state.guestHydrationErrorReport
  );
  const [report, setReport] = useState<GuestHydrationErrorReportData>({
    totalCount: 0,
    guestCount: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    isLastPage: false,
    guestHydrationErrorDetails: [],
  });
  const [guestsPerPage, setGuestsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const [days, setDays] = useState(7);
  const [guestId, setGuestId] = useState("");
  const [searched, setSearched] = useState(false);

  const dayOptions = [7, 30, 365, 9999];

  useEffect(() => {
    setReport({
      totalCount: 0,
      guestCount: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      isLastPage: false,
      guestHydrationErrorDetails: [],
    });
    dispatch(
      getGuestHydrationErrorsAction(
        selectedResort.resortAbbreviation,
        days,
        report.pageSize,
        DEFAULT_PAGE_NUMBER
      )
    );
  }, [selectedResort.resortAbbreviation, days, dispatch, report.pageSize]);

  useEffect(() => {
    if (reportData) {
      setReport(reportData);
    }
  }, [reportData]);

  const copyToClipboard = (guestId: string) =>
    dispatch(
      copyToClipboardAction(
        guestId,
        getResource(resources, "GUEST_HYDRATION_REPORT_COPIED_MASTER_GUEST_ID")
      )
    );

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage);
    dispatch(
      getGuestHydrationErrorsAction(
        selectedResort.resortAbbreviation,
        days,
        guestsPerPage,
        newPage + 1 // API is 1-based
      )
    );
  };

  const handleGuestSearch = () => {
    dispatch(
      getGuestHydrationErrorsAction(
        selectedResort.resortAbbreviation,
        9999,
        guestsPerPage,
        1,
        guestId
      )
    );
    setSearched(true);
  };

  const handleClearClick = () => {
    dispatch(getGuestHydrationErrorsAction(
      selectedResort.resortAbbreviation,
      days,
      guestsPerPage,
      currentPage + 1
    ));
    
    setSearched(false);
  }

  const handleGuestsPerPageSelection = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newSize = parseInt(event.target.value, 10);
    setGuestsPerPage(newSize);
    setCurrentPage(0);
    dispatch(
      getGuestHydrationErrorsAction(
        selectedResort.resortAbbreviation,
        days,
        newSize,
        DEFAULT_PAGE_NUMBER
      )
    );
  };

  const fixedHeightPx = calculateFixedHeight(guestsPerPage);

  return (
    <div css={modalContent}>
      
      
      <div css={tablePagination}>
        <div css={css`margin-right: auto`}>
          <span css={css`margin-right: 5px`}>{getResource(resources, 'GUEST_HYDRATION_REPORT_SEARCH_LABEL')}</span>
          <TextField
          css={guestSearch}
          type='text'
          value={guestId}
          onChange={(e) => setGuestId(e?.target?.value)}
          slotProps={{
            htmlInput: {
              style: {
                padding: '12px 8px',
              },
            },
            input: {            
              endAdornment: (
                <IconButton
                  sx={{ visibility: searched ? "visible" : "hidden" }}
                  onClick={handleClearClick}>
                  <ClearIcon/>
                </IconButton>
              ),
            }
          }}>
          </TextField>
          <BigNeutral onClick={handleGuestSearch} css={css`border-radius: 5px;`}>
            {getResource(resources, 'GUEST_SEARCH_SEARCH')}
          </BigNeutral>
        </div>
        <span>{getResource(resources, 'GUEST_HYDRATION_ERRORS_DAYS_LABEL')}</span>
        <Dropdown
          resources={resources}
          items={dayOptions}
          selectedItem={days}
          onChange={(e) => setDays(e)}
          noItemsMessage="Something went wrong"
          rootCss={css` 
            width: 100px;
            margin-left: 10px;
            height: 30px;
          `}
        ></Dropdown>
        <TablePagination
          component="div"
          count={report?.guestCount | 0}
          page={currentPage}
          onPageChange={handlePageChange}
          rowsPerPage={guestsPerPage}
          onRowsPerPageChange={handleGuestsPerPageSelection}
          labelRowsPerPage={getResource(
            resources,
            "GUEST_HYDRATION_ERRORS_GUESTS_PER_PAGE"
          )}
          rowsPerPageOptions={[5, 10, 15, 25]}
          showFirstButton
          showLastButton
        />
      </div>
      
      <div css={tableWrapper(fixedHeightPx)}>
        {isLoading && (
          <div css={loaderOverlay}>
            <Loader />
          </div>
        )}

        <table css={tableStyle}>
          
          {/* Fix column widths to prevent reflow on scroll bar / content changes */}
          <colgroup>
            <col style={{ width: 375 }} />
            <col style={{ width: 'auto' }} />
            <col style={{ width: 280 }} />
            <col style={{ width: 200 }} />
          </colgroup>

          <thead>
            <tr>
              <th>{getResource(resources, "GUEST_HYDRATION_REPORT_MGID")}</th>
              <th>
                {getResource(resources, "GUEST_HYDRATION_REPORT_HYDRATION_ERROR")}
              </th>
              <th>
                {getResource(resources, "GUEST_HYDRATION_REPORT_HYDRATION_DATE")}
              </th>
            </tr>
          </thead>
          <tbody>
           {
              // A guest can hold more than one error, so the guest id alone is not a unique key. The list is
              // replaced wholesale on load, never reordered.
              report.guestHydrationErrorDetails?.map((detail, index) => (
                <tr key={`${detail.masterGuestId}_${index}`}>
                  <td
                    css={css`
                      width: 375px;
                    `}
                  >
                    <MasterGuestId
                      title={getResource(
                        resources,
                        "GUEST_HYDRATION_REPORT_COPY_MASTER_GUEST_ID"
                      )}
                      onClick={() => copyToClipboard(detail.masterGuestId)}
                    >
                      {detail.masterGuestId}
                    </MasterGuestId>
                  </td>
                  <td
                    title={detail.errorDetails}
                    css={css`
                      width: 800px;
                    `}
                  >
                    {detail.errorDetails}
                  </td>
                  <td
                    css={css`
                      width: 350px;
                      `}>
                    {formatDate(detail.createUtcDateTime, dateFormat)}
                  </td>
                  <td
                    css={css`
                      width: 200px;
                    `}
                  >
                    {detail.errorCount > 0 && (
                      <GuestHydrationErrorReplayButton
                        masterGuestId={detail.masterGuestId}
                        errorCount={detail.errorCount}
                      />
                    )}
                  </td>
                </tr>
              ))
          }
          </tbody>
        </table>
      </div>
    </div>
  );
};
